import React, { useContext, useLayoutEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "utils/useAuth/useAuth";
import { useSnackbar } from "notistack";
import {
  trimObject,
  checkNonMoneyDataForTable,
  checkMoneyDataForTable,
} from "utils/utils";
import { queryPayment, queryTransaction, getLineItems } from "./model";
import { PortalContext } from "App";
import Spin from "components/Spin/Spin";
import s from "./OnlineOrderDetails.module.scss";
import Datatable from "components/Datatable/Datatable";
import useTransactionActionDialogs from "../components/transaction-dialog-hook/useTransactionActionDialogs";
import PaymentDetailSection from "../components/payment-detail-section/PaymentDetailSection";
import TransactionDetailSection from "../components/transaction-detail-section/TransactionDetailSection";
import TextMAS from "components/TextMAS/TextMAS";
import Divider from "components/Divider/Divider";
import { Stack } from "@mui/material";

const PAGE_LOCALE_KEY = "page.transactions.onlineOrderDetails.";

const OnlinePaymentsDetails = ({ path, redirectFn }) => {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const stateContext = useContext(PortalContext);
  const { detailPageState } = stateContext;

  const [isPaymentLoading, setIsPaymentLoading] = useState(true);
  const [isLineitemLoading, setIsLineitemLoading] = useState(true);
  const [isTransactionLoading, setIsTransactionLoading] = useState(true);
  const isLoading =
    isPaymentLoading || isTransactionLoading || isLineitemLoading;

  const [paymentId, setPaymentId] = useState(null);
  const [datasource, setDatasource] = useState({});

  const {
    TransactionActionDialogComponents,
    openReceiptDialog,
    openVoidDialog,
    openRefundDialog,
  } = useTransactionActionDialogs({
    env: "online",
    dialogs: ["receipt", "void", "refund"],
    datasource,
    handleRefetch: () => {
      initialFetchPaymentDetail();
      initialFetchTransactionDetail();
      initialFetchLineItem();
    },
  });

  useLayoutEffect(() => {
    const sessionState = JSON.parse(sessionStorage.getItem("state"));
    if (detailPageState && detailPageState[path]) {
      setPaymentId(detailPageState[path].id);
    } else if (sessionState && sessionState[path]) {
      setPaymentId(sessionState[path].id);
    } else {
      if (paymentId == "") setPaymentId("close");
    }
  }, [detailPageState[path]]);

  useLayoutEffect(() => {
    let isOudated = false;
    const getIsOudated = () => isOudated;

    if (paymentId == "close") {
      redirectFn("close", { tabId: path });
    }

    if (!paymentId) {
      //message "unknown payment id" will be shown
      setIsPaymentLoading(false);
      setIsLineitemLoading(false);
      setIsTransactionLoading(false);
      return;
    }
    fetchDetails(getIsOudated);

    return () => {
      isOudated = true;
    };
  }, [paymentId]);

  const fetchDetails = (getIsOudated) => {
    initialFetchPaymentDetail(getIsOudated);
    initialFetchTransactionDetail(getIsOudated);
    initialFetchLineItem(getIsOudated);
  };

  const initialFetchPaymentDetail = async (getIsOudated) => {
    setIsPaymentLoading(true);
    const apiResponse = await queryPayment(
      trimObject({
        id: paymentId,
      }),
    );
    if (getIsOudated && getIsOudated()) return;
    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode === "0000") {
            setDatasource((state) => ({ ...state, payment: responseBody }));
          } else {
            snackbar(t(PAGE_LOCALE_KEY + "paymentDetails.fetchFail"), "error");
          }
        }
      }
    }

    setIsPaymentLoading(false);
  };

  const initialFetchTransactionDetail = async (getIsOudated) => {
    setIsTransactionLoading(true);
    const apiResponse = await queryTransaction(
      trimObject({
        orderId: paymentId,
      }),
    );
    if (getIsOudated && getIsOudated()) return;

    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode === "0000") {
            setDatasource((state) => ({ ...state, transaction: responseBody }));
          } else {
            snackbar(
              t(PAGE_LOCALE_KEY + "transactionDetails.fetchFail"),
              "error",
            );
          }
        }
      }
    }
    setIsTransactionLoading(false);
  };

  const initialFetchLineItem = async (getIsOudated) => {
    setIsLineitemLoading(true);
    const apiResponse = await getLineItems(
      trimObject({
        id: paymentId,
      }),
    );
    if (getIsOudated && getIsOudated()) return;

    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode === "0000") {
            const parsedItemList = JSON.parse(responseBody.data.lineItems);
            setDatasource((state) => ({ ...state, lineitems: parsedItemList }));
          } else if (responseBody.message === "Not found") {
            snackbar(t(PAGE_LOCALE_KEY + "lineItemDetails.noRecord"), "error");
          } else {
            snackbar(t(PAGE_LOCALE_KEY + "lineItemDetails.fetchFail"), "error");
          }
        }
      }
    }

    setIsLineitemLoading(false);
  };

  const snackbar = (message, variant, durationInMs = 2000) => {
    return enqueueSnackbar(message, {
      variant,
      anchorOrigin: {
        vertical: "top",
        horizontal: "center",
      },
      autoHideDuration: durationInMs,
    });
  };

  // ===================== line items ===================== //
  const lineItems = () => {
    return (
      <div className={s["line-item-list"]}>
        <div className={s["header"]}>
          <TextMAS fontSize="24px" fontWeight="medium" color="brandDark64">
            {t(PAGE_LOCALE_KEY + "lineItemDetails.title")}
          </TextMAS>
        </div>
        <Divider />
        <div className={s["content-container"]}>{lineItemContent()}</div>
      </div>
    );
  };

  const lineItemContent = () => {
    const data = datasource?.lineitems;

    if (!(data instanceof Array) || data.length === 0) {
      return (
        <Stack direction="row" justifyContent="center">
          <TextMAS fontSize="large">
            {t(PAGE_LOCALE_KEY + "lineItemDetails.noRecord")}
          </TextMAS>
        </Stack>
      );
    } else {
      const lineItemJsonList = [];
      data.forEach((parsedItem) => {
        const dumy = {
          unitAmount: parsedItem.priceData.unitAmount.toFixed(2),
          name: parsedItem.priceData.name,
          quantity: parsedItem.quantity,
          amount: (
            parsedItem.priceData.unitAmount * parsedItem.quantity
          ).toFixed(2),
        };
        lineItemJsonList.push(dumy);
      });

      const lineItemColumnList = [
        {
          key: "name",
          title: t(PAGE_LOCALE_KEY + `lineItemDetails.card.content.1`),
          sorter: false,
          render: (data, key) => {
            return checkNonMoneyDataForTable(data, key);
          },
        },
        {
          key: "quantity",
          title: t(PAGE_LOCALE_KEY + `lineItemDetails.card.content.2`),
          sorter: false,
          render: (data, key) => {
            return checkNonMoneyDataForTable(data, key);
          },
        },
        {
          key: "unitAmount",
          title: t(PAGE_LOCALE_KEY + `lineItemDetails.card.content.3`),
          sorter: false,
          render: (data, key) => {
            return checkMoneyDataForTable(data, key);
          },
        },
        ,
        {
          key: "amount",
          title: t(PAGE_LOCALE_KEY + `lineItemDetails.card.content.4`),
          sorter: false,
          render: (data, key) => {
            return checkMoneyDataForTable(data, key);
          },
        },
      ];
      return (
        <Datatable columns={lineItemColumnList} datasource={lineItemJsonList} />
      );
    }
  };
  // ===================== line items end ===================== //

  return (
    <div className={s["container"]}>
      <div className={s["content-container"]}>
        <div className={isLoading ? s["loading-now"] : ""}>
          {isLoading ? (
            <Spin />
          ) : (
            <div className={s["details-container"]}>
              {paymentId && (
                <PaymentDetailSection
                  online
                  payment={datasource?.payment?.data[0]}
                />
              )}
              {paymentId && lineItems()}
              {paymentId && (
                <TransactionDetailSection
                  online
                  transaction={datasource?.transaction?.data}
                  payment={datasource?.payment?.data[0]}
                  openSendReceiptDialog={openReceiptDialog}
                  openVoidTransactionDialog={openVoidDialog}
                  openRefundTransactionDialog={openRefundDialog}
                />
              )}
              {paymentId && TransactionActionDialogComponents}
              {!paymentId && (
                <span>{t(PAGE_LOCALE_KEY + "error.noPaymentId")}</span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default React.memo(OnlinePaymentsDetails);
